import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocalOffer as PromoIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as ActiveIcon,
  Error as InactiveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { JuntoApiService } from '../services/api';
import { logger } from '../utils/logger';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

interface PromoCode {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  applicable_credit_package_ids: string[];
  applicable_membership_ids: string[];
  new_users_only: boolean;
  max_uses?: number;
  current_uses: number;
  max_uses_per_user?: number;
  start_date: string;
  end_date?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

interface CreditPackage {
  id: string;
  name: string;
  package_price: number;
  is_active?: boolean;
}

interface Membership {
  id: string;
  name: string;
  cost: number;
  is_active?: boolean;
}

const PromoCodesPage: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed_amount',
    discount_value: 0,
    applicable_credit_package_ids: [] as string[],
    applicable_membership_ids: [] as string[],
    new_users_only: false,
    max_uses: null as number | null,
    max_uses_per_user: null as number | null,
    start_date: dayjs() as Dayjs,
    end_date: null as Dayjs | null,
  });

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [promoCodesData, creditPackagesData, membershipsData] = await Promise.all([
        JuntoApiService.getPromoCodes(includeInactive),
        JuntoApiService.getCreditPackages(false),
        JuntoApiService.getMemberships(false),
      ]);
      
      setPromoCodes(promoCodesData as any);
      setCreditPackages(creditPackagesData);
      setMemberships(membershipsData);
    } catch (err) {
      setError('Failed to load data');
      logger.error('Error loading data', err as Error);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);


  const openCreateDialog = () => {
    setEditingPromoCode(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      applicable_credit_package_ids: [],
      applicable_membership_ids: [],
      new_users_only: false,
      max_uses: null,
      max_uses_per_user: null,
      start_date: dayjs(),
      end_date: null,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    setFormData({
      code: promoCode.code,
      name: promoCode.name,
      description: promoCode.description || '',
      discount_type: promoCode.discount_type,
      discount_value: promoCode.discount_value,
      applicable_credit_package_ids: promoCode.applicable_credit_package_ids,
      applicable_membership_ids: promoCode.applicable_membership_ids,
      new_users_only: promoCode.new_users_only,
      max_uses: promoCode.max_uses ?? null,
      max_uses_per_user: promoCode.max_uses_per_user ?? null,
      start_date: dayjs(promoCode.start_date),
      end_date: promoCode.end_date ? dayjs(promoCode.end_date) : null,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const promoCodeData = {
        id: editingPromoCode?.id || `promo-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        code: formData.code.toUpperCase(),
        name: formData.name,
        description: formData.description,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        applicable_credit_package_ids: formData.applicable_credit_package_ids,
        applicable_membership_ids: formData.applicable_membership_ids,
        new_users_only: formData.new_users_only,
        max_uses: formData.max_uses,
        current_uses: editingPromoCode?.current_uses || 0,
        max_uses_per_user: formData.max_uses_per_user,
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date ? formData.end_date.toISOString() : null,
        created_at: editingPromoCode?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (editingPromoCode) {
        await JuntoApiService.updatePromoCode(promoCodeData as any);
      } else {
        await JuntoApiService.createPromoCode(promoCodeData as any);
      }

      setDialogOpen(false);
      loadAllData();
    } catch (err) {
      setError('Failed to save promo code');
      logger.error('Error saving promo code', err as Error);
    }
  };


  const filteredPromoCodes = promoCodes.filter(promo =>
    promo.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    promo.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedPromoCodes = filteredPromoCodes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDiscountValue = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `$${value.toFixed(2)}`;
  };

  const getStatusChip = (promoCode: PromoCode) => {
    const now = new Date();
    const startDate = new Date(promoCode.start_date);
    const endDate = promoCode.end_date ? new Date(promoCode.end_date) : null;
    
    if (!promoCode.is_active) {
      return <Chip icon={<InactiveIcon />} label="Inactive" color="error" size="small" />;
    }
    
    if (now < startDate) {
      return <Chip icon={<ScheduleIcon />} label="Scheduled" color="warning" size="small" />;
    }
    
    if (endDate && now > endDate) {
      return <Chip icon={<InactiveIcon />} label="Expired" color="error" size="small" />;
    }
    
    return <Chip icon={<ActiveIcon />} label="Active" color="success" size="small" />;
  };

  const getUsageInfo = (promoCode: PromoCode) => {
    if (!promoCode.max_uses) {
      return `${promoCode.current_uses} uses`;
    }
    return `${promoCode.current_uses}/${promoCode.max_uses} uses`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading promo codes...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4">Promo Codes</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage discount codes for credit packages and memberships
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  icon={<VisibilityOffIcon />}
                  checkedIcon={<VisibilityIcon />}
                />
              }
              label="Show Inactive"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Create Promo Code
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Button size="small" onClick={loadAllData} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PromoIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {promoCodes.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Promo Codes
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ActiveIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {promoCodes.filter(p => p.is_active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Codes
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {promoCodes.filter(p => {
                        const now = new Date();
                        const startDate = new Date(p.start_date);
                        return p.is_active && now < startDate;
                      }).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <InactiveIcon color="error" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {promoCodes.filter(p => {
                        const now = new Date();
                        const endDate = p.end_date ? new Date(p.end_date) : null;
                        return !p.is_active || (endDate && now > endDate);
                      }).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive/Expired
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search promo codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Promo Codes Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Applies To</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPromoCodes.map((promoCode) => (
                <TableRow key={promoCode.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PromoIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="bold">
                        {promoCode.code}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{promoCode.name}</Typography>
                    {promoCode.new_users_only && (
                      <Chip label="New Users Only" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDiscountValue(promoCode.discount_type, promoCode.discount_value)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {promoCode.applicable_credit_package_ids.length > 0 && (
                        <Chip label={`${promoCode.applicable_credit_package_ids.length} Packages`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      )}
                      {promoCode.applicable_membership_ids.length > 0 && (
                        <Chip label={`${promoCode.applicable_membership_ids.length} Memberships`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getUsageInfo(promoCode)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(promoCode)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(promoCode.start_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {promoCode.end_date ? new Date(promoCode.end_date).toLocaleDateString() : 'No expiry'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={promoCode}
                      entityType="promo-codes"
                      entityDisplayName="Promo Code"
                      onEdit={openEditDialog}
                      onRefresh={loadAllData}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Code:</strong> {entity.code}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Discount:</strong> {formatDiscountValue(entity.discount_type, entity.discount_value)}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Uses:</strong> {getUsageInfo(entity)}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPromoCodes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingPromoCode ? 'Edit Promo Code' : 'Create Promo Code'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="WELCOME20"
                  helperText="The code customers will enter (will be converted to uppercase)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Welcome Discount"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description of the promo code"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed_amount' })}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Discount Value"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: formData.discount_type === 'percentage' ? 1 : 0.01 }}
                  helperText={formData.discount_type === 'percentage' ? 'Enter percentage (e.g., 20 for 20%)' : 'Enter dollar amount (e.g., 10.00 for $10)'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={creditPackages}
                  getOptionLabel={(option) => option.name}
                  value={creditPackages.filter(pkg => formData.applicable_credit_package_ids.includes(pkg.id))}
                  onChange={(_, newValue) => setFormData({ 
                    ...formData, 
                    applicable_credit_package_ids: newValue.map(pkg => pkg.id) 
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applicable Credit Packages"
                      placeholder="Select packages"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={memberships}
                  getOptionLabel={(option) => option.name}
                  value={memberships.filter(membership => formData.applicable_membership_ids.includes(membership.id))}
                  onChange={(_, newValue) => setFormData({ 
                    ...formData, 
                    applicable_membership_ids: newValue.map(membership => membership.id) 
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applicable Memberships"
                      placeholder="Select memberships"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Uses"
                  type="number"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  inputProps={{ min: 1 }}
                  helperText="Leave empty for unlimited uses"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Uses Per User"
                  type="number"
                  value={formData.max_uses_per_user || ''}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value ? parseInt(e.target.value) : null })}
                  inputProps={{ min: 1 }}
                  helperText="Leave empty for unlimited uses per user"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(newValue) => setFormData({ ...formData, start_date: newValue || dayjs() })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="End Date"
                  value={formData.end_date}
                  onChange={(newValue) => setFormData({ ...formData, end_date: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth helperText="Leave empty for no expiry" />}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.new_users_only}
                      onChange={(e) => setFormData({ ...formData, new_users_only: e.target.checked })}
                    />
                  }
                  label="New Users Only"
                />
                <FormHelperText>
                  If checked, only users who have never made a purchase can use this code
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>
                  Inactive promo codes cannot be used
                </FormHelperText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingPromoCode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PromoCodesPage;